import { ReactNode, useCallback, useMemo } from "react";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { Invoice } from "@common/types/invoice";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { InjectedCommandOption } from "@components/commands/lib";
import { toInvoiceBulkScheduleRow } from "@components/accounting/schedules/bulk/invoiceBulkScheduleRow";
import { canOfferTemplateForDocument } from "@components/accounting/schedules/documentScheduleTemplates";
import { useBulkScheduleApply } from "@components/accounting/schedules/bulk/useBulkScheduleApply";
import { resolveInvoiceTemplateBlockReason } from "@components/accounting/schedules/scheduleApplyConfirmHelpers";

const TOAST_KEY = "invoices-bulk-schedule";

interface UseInvoiceBulkScheduleOptionsParams {
    selectedInvoices: Invoice[];
    isEnabled: boolean;
    isBusy: boolean;
    onClearSelection?: () => void;
}

export function useInvoiceBulkScheduleOptions({
    selectedInvoices,
    isEnabled,
    isBusy,
    onClearSelection,
}: UseInvoiceBulkScheduleOptionsParams): {
    options: InjectedCommandOption[];
    confirmModal: ReactNode;
} {
    const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();
    const rows = useMemo(
        () => selectedInvoices.map(toInvoiceBulkScheduleRow),
        [selectedInvoices],
    );
    const isTemplateEligible = useCallback(
        (template: ScheduleTemplateListItem) =>
            canOfferTemplateForDocument({
                template,
                documentType: ScheduleSourceType.invoice,
                isExpensesEnabled,
            }),
        [isExpensesEnabled],
    );
    return useBulkScheduleApply({
        rows,
        noun: "invoice",
        isEnabled,
        isFieldLocked: isBusy,
        templateBlockReason: resolveInvoiceTemplateBlockReason,
        isTemplateEligible,
        toastKey: TOAST_KEY,
        invalidate: async () => {
            await invalidateInvoiceQueries();
        },
        clearSelection: () => onClearSelection?.(),
    });
}
